import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Plus, Trash2, FileDown } from 'lucide-react';
import { getLanguageName } from '../constants/languages';
import { roundMoney } from '../utils/rateCalc';
import './InvoiceModal.css';

// A line's amount, rounded to 2 decimals ONCE here. Both the on-screen line,
// the invoice total, and the PDF read this same value, so the printed lines
// always sum to the printed total (no per-layer re-rounding drift).
const lineAmount = (it) => roundMoney((Number(it.quantity) || 0) * (Number(it.rate) || 0));

const PROVIDER_KEY = 'cc_invoice_provider_v1';

function loadProvider() {
  try {
    const raw = window.localStorage.getItem(PROVIDER_KEY);
    if (!raw) return { name: '', details: '' };
    const p = JSON.parse(raw);
    return { name: p.name || '', details: p.details || '' };
  } catch {
    return { name: '', details: '' };
  }
}

function saveProvider(provider) {
  try {
    window.localStorage.setItem(PROVIDER_KEY, JSON.stringify(provider));
  } catch {
    /* private mode / quota - provider just won't persist, form still works */
  }
}

function todayISO() {
  const d = new Date();
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

function defaultNumber() {
  return `INV-${todayISO().replace(/-/g, '')}`;
}

// Seed one line item per priced target. The translator trims/edits from there.
function seedItems(fileName, sourceLang, priceRows, t) {
  const unitLabel = (unit) => (unit === 'word' ? t('invoice.unitWord') : t('invoice.unitPage'));
  const src = getLanguageName(sourceLang);
  return priceRows.map((row) => ({
    description: `${fileName || t('invoice.document')} · ${src} -> ${getLanguageName(row.targetLang)}`,
    quantity: Math.round(row.quantity * 10) / 10,
    unit: unitLabel(row.unit),
    rate: row.rate,
  }));
}

export default function InvoiceModal({ open, onClose, fileName, sourceLang, priceRows, currency }) {
  const { t } = useTranslation('calculator');
  const [provider, setProvider] = useState(loadProvider);
  const [client, setClient] = useState({ name: '', details: '' });
  const [number, setNumber] = useState(defaultNumber);
  const [date, setDate] = useState(todayISO);
  const [items, setItems] = useState(() => seedItems(fileName, sourceLang, priceRows, t));
  const [downloading, setDownloading] = useState(false);

  const total = useMemo(
    () => roundMoney(items.reduce((sum, it) => sum + lineAmount(it), 0)),
    [items]
  );

  if (!open) return null;

  const updateProvider = (field, value) => {
    const next = { ...provider, [field]: value };
    setProvider(next);
    saveProvider(next);
  };
  const updateItem = (i, field, value) =>
    setItems((prev) => prev.map((it, idx) => (idx === i ? { ...it, [field]: value } : it)));
  const addItem = () =>
    setItems((prev) => [...prev, { description: '', quantity: 0, unit: t('invoice.unitWord'), rate: 0 }]);
  const removeItem = (i) => setItems((prev) => prev.filter((_, idx) => idx !== i));

  const fmt = (n) => (Math.round((Number(n) || 0) * 100) / 100).toLocaleString();

  const onDownload = async () => {
    setDownloading(true);
    try {
      const [{ default: pdfMake }, { default: pdfFonts }] = await Promise.all([
        import('pdfmake/build/pdfmake'),
        import('pdfmake/build/vfs_fonts'),
      ]);
      pdfMake.vfs = pdfFonts.vfs || pdfFonts.pdfMake?.vfs || pdfFonts;
      const { downloadInvoicePdf } = await import('../utils/invoicePdf');
      const invoice = {
        number,
        date,
        provider,
        client,
        currency,
        items: items.map((it) => ({
          description: it.description,
          quantity: Number(it.quantity) || 0,
          unit: it.unit,
          rate: Number(it.rate) || 0,
          amount: lineAmount(it),
        })),
        total,
      };
      const labels = {
        title: t('invoice.title'),
        number: t('invoice.numberLabel'),
        date: t('invoice.dateLabel'),
        from: t('invoice.from'),
        to: t('invoice.to'),
        description: t('invoice.description'),
        quantity: t('invoice.quantity'),
        rate: t('invoice.rate'),
        amount: t('invoice.amount'),
        total: t('invoice.total'),
        footer: t('invoice.footer'),
      };
      downloadInvoicePdf(pdfMake, invoice, labels, `${number || 'invoice'}.pdf`);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="inv-backdrop" role="dialog" aria-modal="true" onClick={onClose}>
      <div className="inv-modal" onClick={(e) => e.stopPropagation()}>
        <button type="button" className="inv-close" onClick={onClose} aria-label={t('invoice.close')}>
          <X size={18} />
        </button>
        <h2 className="inv-title">{t('invoice.title')}</h2>

        <div className="inv-meta">
          <label className="inv-field">
            <span>{t('invoice.numberLabel')}</span>
            <input className="input" value={number} onChange={(e) => setNumber(e.target.value)} />
          </label>
          <label className="inv-field">
            <span>{t('invoice.dateLabel')}</span>
            <input className="input" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
          </label>
        </div>

        <div className="inv-parties">
          <div className="inv-party">
            <div className="inv-party-label">{t('invoice.from')}</div>
            <input
              className="input"
              placeholder={t('invoice.namePh')}
              value={provider.name}
              onChange={(e) => updateProvider('name', e.target.value)}
            />
            <textarea
              className="input"
              placeholder={t('invoice.detailsPh')}
              value={provider.details}
              onChange={(e) => updateProvider('details', e.target.value)}
            />
            <span className="inv-hint">{t('invoice.providerSaved')}</span>
          </div>
          <div className="inv-party">
            <div className="inv-party-label">{t('invoice.to')}</div>
            <input
              className="input"
              placeholder={t('invoice.clientNamePh')}
              value={client.name}
              onChange={(e) => setClient({ ...client, name: e.target.value })}
            />
            <textarea
              className="input"
              placeholder={t('invoice.detailsPh')}
              value={client.details}
              onChange={(e) => setClient({ ...client, details: e.target.value })}
            />
          </div>
        </div>

        <div className="inv-items">
          <div className="inv-item inv-item-head">
            <span>{t('invoice.description')}</span>
            <span>{t('invoice.quantity')}</span>
            <span>{t('invoice.rate')}</span>
            <span>{t('invoice.amount')}</span>
            <span />
          </div>
          {items.map((it, i) => (
            <div className="inv-item" key={i}>
              <input className="input" value={it.description} onChange={(e) => updateItem(i, 'description', e.target.value)} />
              <div className="inv-qty">
                <input className="input" type="number" min="0" step="0.1" value={it.quantity} onChange={(e) => updateItem(i, 'quantity', e.target.value)} />
                <input className="input inv-unit" value={it.unit} onChange={(e) => updateItem(i, 'unit', e.target.value)} />
              </div>
              <input className="input" type="number" min="0" step="0.001" value={it.rate} onChange={(e) => updateItem(i, 'rate', e.target.value)} />
              <span className="inv-amount">
                {fmt(lineAmount(it))} {currency}
              </span>
              <button type="button" className="inv-remove" onClick={() => removeItem(i)} aria-label={t('invoice.removeLine')}>
                <Trash2 size={15} />
              </button>
            </div>
          ))}
          <button type="button" className="inv-add" onClick={addItem}>
            <Plus size={15} /> {t('invoice.addLine')}
          </button>
        </div>

        <div className="inv-total">
          <span>{t('invoice.total')}</span>
          <strong>{fmt(total)} {currency}</strong>
        </div>

        <div className="inv-actions">
          <button type="button" className="btn btn-primary" onClick={onDownload} disabled={downloading}>
            <FileDown size={16} /> {downloading ? t('invoice.generating') : t('invoice.downloadPdf')}
          </button>
        </div>
      </div>
    </div>
  );
}
